import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useCallback } from 'react';
import { useTheme, Button, DataGrid, GridRow, Icon, Spacer, Tooltip, Typography, Select, MenuItem, SearchField } from '@emburse/embark-core';
import { CheckIcon, ErrorIcon, SyncIcon } from '@emburse/embark-icons';
import { formatDateTime, getTimezone, calculateSeconds } from '../../../util/dateAdapter';
import { $t, useTranslation } from '../../../hooks/useTranslation';
var RunStatus;
(function (RunStatus) {
    RunStatus["Complete"] = "Complete";
    RunStatus["Error"] = "RunError";
})(RunStatus || (RunStatus = {}));
function HistoryTable({ loading, data, updateData }) {
    useTranslation();
    const theme = useTheme();
    const [filterLoading, setFilterLoading] = useState(false);
    const [filteredData, setFilteredData] = useState([]);
    const [isFilteringData, setIsFilteringData] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    useEffect(() => {
        if (searchFilter.length === 0 && (!statusFilter || statusFilter.length === 0)) {
            setFilteredData([]);
            setIsFilteringData(false);
        }
        else {
            setFilteredData(data.filter((value) => (searchFilter.length
                ? value.DataInterchangeName.toLowerCase().includes(searchFilter.toLowerCase())
                : true) &&
                (statusFilter.length ? value.RunStatus === RunStatus[statusFilter] : true)));
            setIsFilteringData(true);
        }
        setFilterLoading(false);
    }, [statusFilter, searchFilter, data]);
    const searchChangeHandler = useCallback((value) => {
        if (!filterLoading) {
            setFilterLoading(true);
        }
        setSearchFilter(value);
    }, [filterLoading]);
    const updateStatusFilter = (newFilter) => {
        if (!filterLoading) {
            setFilterLoading(true);
        }
        setStatusFilter(newFilter === 'All' ? '' : newFilter);
    };
    return (_jsxs(_Fragment, { children: [_jsx(Spacer, { size: 30 }), _jsxs(GridRow, { style: { columnGap: 8 }, children: [_jsx(SearchField, { label: $t('Search'), id: "history-search-input", disabled: loading, doSearchOnChange: true, onChangeTimeout: 250, searchAction: searchChangeHandler }), _jsxs(Select, { id: "status-flter-select", value: statusFilter && statusFilter.length ? statusFilter : 'All', label: $t('Status'), onChange: (e) => updateStatusFilter(e.target.value), children: [_jsx(MenuItem, { value: "All", children: $t('All') }, "All"), Object.keys(RunStatus).map((status) => (_jsx(MenuItem, { value: status, children: status }, status)))] })] }), _jsx(Spacer, { size: 20 }), _jsx(Button, { endIcon: _jsx(Icon, { iconUrl: SyncIcon }), size: "small", variant: "outlined", onClick: () => {
                    if (!loading) {
                        updateData();
                    }
                }, children: $t('Refresh') }), _jsx(Spacer, { size: 30 }), _jsx(DataGrid, { loading: loading || filterLoading, autoHeight: true, pageSizeOptions: [5, 10, 20], columns: [
                    {
                        field: 'DataInterchangeName',
                        headerName: $t('Name'),
                        width: 270,
                        renderCell: (cell) => (_jsx(Tooltip, { title: cell.value, children: _jsx(Typography, { noWrap: true, children: cell.value }) }))
                    },
                    {
                        field: 'FileName',
                        headerName: $t('File'),
                        width: 270,
                        renderCell: (cell) => _jsx(Typography, { noWrap: true, children: cell.value })
                    },
                    {
                        field: 'FileSize',
                        headerName: $t('File Size'),
                        width: 120,
                        renderCell: (cell) => (_jsxs(Typography, { children: [parseInt(cell.value, 10).toLocaleString(), " ", $t('bytes')] }))
                    },
                    {
                        field: 'StartTime',
                        headerName: $t('Last Run Time'),
                        width: 410,
                        sortComparator: (v1, v2) => {
                            const lastDate = new Date(v1);
                            const nextDate = new Date(v2);
                            return lastDate.getTime() - nextDate.getTime();
                        },
                        renderCell: (cell) => {
                            const startTime = new Date(cell.row.StartTime);
                            const endTime = new Date(cell.row.EndTime);
                            const duration = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
                            const renderedDate = () => `${formatDateTime(startTime)} ${getTimezone(startTime)} - ${calculateSeconds(duration)}`;
                            const dateTooltip = () => `Run Ended: ${formatDateTime(endTime)} ${getTimezone(startTime)}`;
                            return (_jsx(Tooltip, { title: dateTooltip(), placement: "right", children: _jsx(Typography, { children: renderedDate() }) }));
                        }
                    },
                    {
                        field: 'RunStatus',
                        headerName: $t('Status'),
                        width: 120,
                        renderCell: (cell) => cell.value === RunStatus.Complete ? (_jsxs(GridRow, { wrap: "nowrap", children: [_jsx(Icon, { style: {
                                        color: theme.palette.success.main,
                                        marginRight: 10
                                    }, iconUrl: CheckIcon }), _jsx(Typography, { display: "inline", children: $t('Complete') })] })) : (_jsxs(GridRow, { wrap: "nowrap", children: [_jsx(Icon, { style: { color: theme.palette.error.main, marginRight: 10 }, iconUrl: ErrorIcon }), _jsx(Typography, { display: "inline", children: $t('Error') })] }))
                    }
                ], rows: isFilteringData ? filteredData : data })] }));
}
export default HistoryTable;
